// Startbesked: samlingsdokument över vilka arbetsberedningar som fått klart för start.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PlayCircle, FileDown, RefreshCw, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import DocumentHeader from "@/components/BasProcess/DocumentHeader";
import { generateClearanceWord } from "@/lib/documentGenerator/granskningWordGenerator";

interface Props {
  projectId: string;
  projectName?: string;
  canExport?: boolean;
}

interface WpRow {
  id: string;
  name: string;
  review_status: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  updated_at: string | null;
  stage_id: string | null;
  data: any;
}

const STATUS_LABELS: Record<string, string> = {
  godkand: "Klart för start",
  inskickad: "Inskickad – väntar på granskning",
  komplettering: "Kompletteras",
  utkast: "Utkast",
};

const WorkPrepClearance: React.FC<Props> = ({ projectId, projectName, canExport = true }) => {
  const [rows, setRows] = useState<WpRow[]>([]);
  const [stages, setStages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [wp, st] = await Promise.all([
      supabase
        .from("work_preparations")
        .select("id, name, review_status, reviewed_at, reviewed_by, updated_at, stage_id, data")
        .eq("project_id", projectId)
        .is("deleted_at", null)
        .order("updated_at", { ascending: false }),
      supabase.from("project_stages").select("id, name").eq("project_id", projectId),
    ]);
    setRows((wp.data ?? []) as WpRow[]);
    setStages(Object.fromEntries((st.data ?? []).map((s: any) => [s.id, s.name])));
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const enriched = useMemo(
    () =>
      rows.map((r) => {
        const fields = (r.data?.fields ?? {}) as any;
        const comps = (fields.kompetensVerifieringar ?? []) as any[];
        const sheets = (fields.sakerhetsdatablad ?? []) as any[];
        const expired = comps.filter(
          (k) => k.giltigTill && new Date(k.giltigTill) < new Date(new Date().toDateString())
        );
        const missingSheets = sheets.filter((s) => !s.harFil);
        const warnings = [
          expired.length ? `${expired.length} utgånget kompetensbevis` : "",
          missingSheets.length ? `${missingSheets.length} produkt saknar säkerhetsdatablad` : "",
        ].filter(Boolean);
        return {
          ...r,
          contractor: fields.gjordAv ?? "",
          start: fields.startdatum ?? "",
          end: fields.slutdatum ?? "",
          stage: r.stage_id ? stages[r.stage_id] ?? "" : "",
          warning: warnings.join(" · "),
        };
      }),
    [rows, stages]
  );

  const cleared = enriched.filter((r) => r.review_status === "godkand");
  const waiting = enriched.filter((r) => r.review_status !== "godkand");

  const exportWord = async () => {
    try {
      await generateClearanceWord({
        rows: enriched.map((r) => ({
          name: r.name,
          contractor: r.contractor,
          stage: r.stage,
          start: r.start,
          end: r.end,
          statusLabel: STATUS_LABELS[r.review_status ?? "utkast"] ?? "Utkast",
          approvedBy: r.reviewed_by ?? "",
          approvedAt: r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString("sv-SE") : "",
          warning: r.warning,
        })),
        projectName,
      });
      toast.success("Startbeskedet exporterat");
    } catch {
      toast.error("Kunde inte exportera startbeskedet");
    }
  };

  const renderList = (list: typeof enriched, emptyText: string) => (
    <CardContent className="space-y-2">
      {list.length === 0 && <p className="text-sm text-muted-foreground">{emptyText}</p>}
      {list.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm">
          <div className="min-w-0">
            <div className="font-medium">{r.name}</div>
            <div className="text-xs text-muted-foreground">
              {[r.contractor, r.stage, [r.start, r.end].filter(Boolean).join(" – ")].filter(Boolean).join(" · ") ||
                "Ingen period angiven"}
            </div>
            {r.warning && (
              <div className="mt-1 flex items-center gap-1 text-xs text-destructive">
                <AlertTriangle className="h-3 w-3" /> {r.warning}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge
              className={r.review_status === "godkand" ? "bg-emerald-600 hover:bg-emerald-600" : ""}
              variant={r.review_status === "godkand" ? "default" : "secondary"}
            >
              {STATUS_LABELS[r.review_status ?? "utkast"] ?? "Utkast"}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString("sv-SE") : ""}
            </span>
          </div>
        </div>
      ))}
    </CardContent>
  );

  return (
    <div className="space-y-6">
      <DocumentHeader
        docNumber="24b"
        title="Startbesked arbetsberedningar"
        description="Samlingsdokument över alla arbetsberedningar i projektet och vilka som har fått klart för start av Bas-U."
        icon={PlayCircle}
        projectId={projectId}
        meta={
          <Badge variant="outline">
            {cleared.length} av {enriched.length} klara för start
          </Badge>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Uppdatera
            </Button>
            {canExport && (
              <Button size="sm" onClick={exportWord}>
                <FileDown className="mr-1 h-4 w-4" /> Exportera till Word
              </Button>
            )}
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Klart för start ({cleared.length})</CardTitle>
        </CardHeader>
        {renderList(cleared, "Ingen arbetsberedning är ännu godkänd av Bas-U.")}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Väntar på godkännande ({waiting.length})</CardTitle>
        </CardHeader>
        {renderList(waiting, "Alla arbetsberedningar är godkända.")}
      </Card>
    </div>
  );
};

export default WorkPrepClearance;
